import { ForwardedRef, forwardRef, KeyboardEvent, ReactNode } from 'react';

import { cn } from '@foundation/utils/cn';

import { AdvancedSelectOption } from './types';
import { ChevronDownIcon } from '../../../icons';

interface AdvancedSelectTriggerProps<T = string> {
  disabled: boolean;
  hasError?: boolean;
  className?: string;
  label?: string;
  value?: T;
  required?: boolean;
  selectedOption?: AdvancedSelectOption<T>;
  renderTrigger?: (
    selectedOption: AdvancedSelectOption<T> | undefined,
  ) => ReactNode;
  onOpen: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLButtonElement>) => void;
  'data-testid'?: string;
}

const defaultRenderTrigger = <T,>(
  selectedOption: AdvancedSelectOption<T> | undefined,
  placeholder?: string,
) => {
  if (selectedOption)
    return <span className="text-primary">{selectedOption.label}</span>;
  if (placeholder) {
    return <span className="text-tertiary">{placeholder}</span>;
  }
  return null;
};

function AdvancedSelectTriggerInner<T = string>(
  {
    disabled,
    hasError,
    className = '',
    label,
    value,
    required,
    selectedOption,
    renderTrigger,
    onOpen,
    onKeyDown,
    'data-testid': dataTestId,
  }: AdvancedSelectTriggerProps<T>,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={() => !disabled && onOpen()}
      onKeyDown={onKeyDown}
      disabled={disabled}
      className={cn(
        'w-full min-h-[56px] px-4 py-1 border rounded-lg flex justify-between gap-1 focus:outline-none cursor-pointer bg-white',
        hasError
          ? 'border-alert-negative-primary ring-1 ring-inset ring-[var(--text-alert-negative-primary)]'
          : cn(
              'border-primary',
              'focus:border-[var(--brand)] focus:ring-1 focus:ring-inset focus:ring-[var(--brand)]',
              'active:border-[var(--brand)] active:ring-1 active:ring-inset active:ring-[var(--brand)]',
            ),
        disabled &&
          'cursor-not-allowed bg-pmui-interactive-bg-disabled text-pmui-interactive-fg-disabled',
        className,
      )}
      data-testid={dataTestId}
    >
      <div className="flex flex-col flex-1 ">
        {label && value && (
          <span className="text-xs font-normal font-body text-left">
            {label}
            {required && (
              <span className="text-md text-alert-negative-primary">
                &nbsp;*
              </span>
            )}
          </span>
        )}
        <div className="flex items-center gap-2 flex-1">
          {renderTrigger
            ? renderTrigger(selectedOption)
            : defaultRenderTrigger(selectedOption, label)}
        </div>
      </div>
      <ChevronDownIcon className="w-4 h-4 self-center" />
    </button>
  );
}

export const AdvancedSelectTrigger = forwardRef(AdvancedSelectTriggerInner) as <
  T = string,
>(
  props: AdvancedSelectTriggerProps<T> & {
    ref?: ForwardedRef<HTMLButtonElement>;
  },
) => ReturnType<typeof AdvancedSelectTriggerInner>;
